import React from 'react';
import { Wifi, Gauge, Clock } from 'lucide-react';

const PlatformHealthMatrix = ({ platforms = [] }) => {
  const getHealthColor = (value) => {
    if (value >= 90) return 'bg-green-500';
    if (value >= 70) return 'bg-yellow-500';
    if (value >= 50) return 'bg-orange-500';
    return 'bg-red-500';
  };

  const getHealthTextColor = (value) => {
    if (value >= 90) return 'text-green-700';
    if (value >= 70) return 'text-yellow-700';
    if (value >= 50) return 'text-orange-700';
    return 'text-red-700';
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Platform Health Matrix</h2>
          <p className="text-sm text-gray-600 mt-1">
            API connectivity, rate limits, and data freshness indicators
          </p>
        </div>
        <Wifi className="w-6 h-6 text-blue-600" />
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-200">
              <th className="text-left py-3 px-4 text-sm font-semibold text-gray-900">
                Platform
              </th>
              <th className="text-center py-3 px-4 text-sm font-semibold text-gray-900">
                <div className="flex items-center justify-center gap-2">
                  <Wifi className="w-4 h-4" />
                  Connectivity
                </div>
              </th>
              <th className="text-center py-3 px-4 text-sm font-semibold text-gray-900">
                <div className="flex items-center justify-center gap-2">
                  <Gauge className="w-4 h-4" />
                  Rate Limit
                </div>
              </th>
              <th className="text-center py-3 px-4 text-sm font-semibold text-gray-900">
                <div className="flex items-center justify-center gap-2">
                  <Clock className="w-4 h-4" />
                  Freshness
                </div>
              </th>
              <th className="text-center py-3 px-4 text-sm font-semibold text-gray-900">
                Overall Health
              </th>
            </tr>
          </thead>
          <tbody>
            {platforms?.map((platform, index) => {
              const overallHealth = Math.round(
                (platform?.connectivity + (100 - platform?.rateLimit) + platform?.freshness) / 3
              );

              return (
                <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="py-4 px-4">
                    <span className="font-medium text-gray-900">{platform?.platform}</span>
                  </td>
                  {/* Connectivity */}
                  <td className="py-4 px-4">
                    <div className="flex items-center justify-center gap-3">
                      <div className="flex-1 max-w-[100px]">
                        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div 
                            className={`h-full ${getHealthColor(platform?.connectivity)} transition-all duration-500`}
                            style={{ width: `${platform?.connectivity}%` }}
                          />
                        </div>
                      </div>
                      <span className={`text-sm font-semibold ${getHealthTextColor(platform?.connectivity)}`}>
                        {platform?.connectivity}%
                      </span>
                    </div>
                  </td>
                  {/* Rate Limit */}
                  <td className="py-4 px-4">
                    <div className="flex items-center justify-center gap-3">
                      <div className="flex-1 max-w-[100px]">
                        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div 
                            className={`h-full ${
                              platform?.rateLimit > 80 ? 'bg-red-500' :
                              platform?.rateLimit > 60 ? 'bg-yellow-500' : 'bg-green-500'
                            } transition-all duration-500`}
                            style={{ width: `${platform?.rateLimit}%` }}
                          />
                        </div>
                      </div>
                      <span className={`text-sm font-semibold ${
                        platform?.rateLimit > 80 ? 'text-red-700' :
                        platform?.rateLimit > 60 ? 'text-yellow-700' : 'text-green-700'
                      }`}>
                        {platform?.rateLimit}%
                      </span>
                    </div>
                  </td>
                  {/* Freshness */}
                  <td className="py-4 px-4">
                    <div className="flex items-center justify-center gap-3">
                      <div className="flex-1 max-w-[100px]">
                        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div 
                            className={`h-full ${getHealthColor(platform?.freshness)} transition-all duration-500`}
                            style={{ width: `${platform?.freshness}%` }}
                          />
                        </div>
                      </div>
                      <span className={`text-sm font-semibold ${getHealthTextColor(platform?.freshness)}`}>
                        {platform?.freshness}%
                      </span>
                    </div>
                  </td>
                  {/* Overall Health */}
                  <td className="py-4 px-4">
                    <div className="flex items-center justify-center">
                      <div className={`px-3 py-1 rounded-full text-sm font-semibold ${
                        overallHealth >= 90 ? 'bg-green-100 text-green-800' :
                        overallHealth >= 70 ? 'bg-yellow-100 text-yellow-800' :
                        overallHealth >= 50 ? 'bg-orange-100 text-orange-800': 'bg-red-100 text-red-800'
                      }`}>
                        {overallHealth}%
                      </div>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {/* Legend */}
      <div className="mt-6 pt-4 border-t border-gray-200">
        <h4 className="text-sm font-semibold text-gray-900 mb-3">Health Indicators</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Wifi className="w-4 h-4 text-blue-600" />
              <span className="font-medium text-gray-700">API Connectivity</span>
            </div>
            <p className="text-gray-600">Status of API connection and authentication</p>
          </div>
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Gauge className="w-4 h-4 text-orange-600" />
              <span className="font-medium text-gray-700">Rate Limit Utilization</span>
            </div>
            <p className="text-gray-600">Percentage of API quota consumed (lower is better)</p>
          </div>
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Clock className="w-4 h-4 text-green-600" />
              <span className="font-medium text-gray-700">Data Freshness</span>
            </div>
            <p className="text-gray-600">How recent the collected data is</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlatformHealthMatrix;